import React from 'react';
import { Box, useTheme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface ReportEditModeContentProps {
  editableContent: string;
  isTranslating: boolean;
  isAutoFormatting: boolean;
  cleanMarkdownContent: (content: string) => string;
}

const ReportEditModeContent: React.FC<ReportEditModeContentProps> = ({
  editableContent,
  isTranslating,
  isAutoFormatting,
  cleanMarkdownContent,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
        display: 'flex',
        justifyContent: 'center',
        py: { xs: 3, md: 6 },
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
        // Mobile: constrain to viewport minus header (60px) and footer (180px) = 240px total
        maxHeight: { xs: 'calc(100vh - 240px)', md: 'none' },
        height: { xs: 'calc(100vh - 240px)', md: 'auto' },
        position: 'relative',
      }}
    >

      <Box
        sx={{
          width: '100%',
          maxWidth: '900px',
          px: { xs: 3, sm: 4, md: 6 },
          py: 5,
          pb: { xs: 8, md: 5 },
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontSize: '16px',
          lineHeight: 1.7,
          color: theme.palette.text.primary,
          '& h1': {
            fontSize: { xs: '2.25rem', md: '2.75rem' },
            fontWeight: 700,
            mt: 0,
            mb: 3,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            color: theme.palette.text.primary,
          },
          '& h2': {
            fontSize: { xs: '1.875rem', md: '2.25rem' },
            fontWeight: 600,
            mt: 4,
            mb: 2.5,
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
            color: theme.palette.text.primary,
          },
          '& h3': {
            fontSize: { xs: '1.5rem', md: '1.875rem' },
            fontWeight: 600,
            mt: 3,
            mb: 2,
            lineHeight: 1.4,
            color: theme.palette.text.primary,
          },
          '& p': {
            mb: 2.5,
            lineHeight: 1.8,
          },
          '& strong': {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          '& code': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
            padding: '3px 8px',
            borderRadius: '6px',
            fontSize: '0.9em',
            fontFamily: '"SF Mono", Monaco, Menlo, monospace',
            color: theme.palette.mode === 'dark' ? '#FF6B6B' : '#C73E3E',
          },
          '& pre': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            padding: 3,
            borderRadius: 2,
            overflow: 'auto',
            mb: 3,
            border: `1px solid ${theme.palette.divider}`,
          },
          '& blockquote': {
            borderLeft: `4px solid #007AFF`,
            pl: 3,
            ml: 0,
            my: 3,
            fontStyle: 'italic',
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,122,255,0.08)' : 'rgba(0,122,255,0.05)',
            py: 2,
            borderRadius: '0 8px 8px 0',
          },
          '& ul, & ol': {
            pl: 3,
            mb: 2.5,
          },
          '& li': {
            mb: 1,
            lineHeight: 1.8,
          },
          '& a': {
            color: '#007AFF',
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '5px',
            '&:hover': {
              background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <SyntaxHighlighter
                  style={vscDarkPlus}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
          }}
        >
          {cleanMarkdownContent(editableContent)}
        </ReactMarkdown>
      </Box>
    </Box>
  );
};

export default ReportEditModeContent;
